import debug from 'debug';

const log = debug(`video:consent`);

/**
 * Type definitions for the TCFv2 API (see [1]), which we assume exists in prior to
 * this script being run.
 * `window.__tcfapi`
 *
 * - [1] https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/IAB%20Tech%20Lab%20-%20CMP%20API%20v2.md
 */

/**
 * @typedef {Window & {__tcfapi?: TCFv2API}} ExtendedWindow
 */

/**
 * @typedef {2} TCFVersion
 *
 * @callback EventHandler
 * @param {TCData} tcData
 * @param {boolean} success
 * @return {void}
 *
 * @typedef {object} TCData
 * @prop {Boolean | undefined} gdprApplies
 * @prop {Event} eventStatus
 * @prop {CmpStatus} cmpStatus
 * @prop {Number} listenerId
 * @prop {String} tcString
 * @prop {Number} cmpId
 * @prop {Number} cmpVersion
 */

/**
 * @typedef {(command: 'addEventListener', version: TCFVersion, h: EventHandler) => void} AddEventListenerCmd
 * @typedef {(command: 'removeEventListener', version: TCFVersion, listenerId: number) => void} RemoveEventListenerCmd
 * @typedef {(command: 'ping', version: TCFVersion, listenerId: number) => void} PingCmd
 * @typedef {(command: 'getTCData', version: TCFVersion, h: EventHandler, vendorIds?: number[]) => void} GetTCDataCmd
 * @typedef {AddEventListenerCmd & RemoveEventListenerCmd & PingCmd & GetTCDataCmd} TCFv2API
 */

/** @enum {string} Event */
export const Event = {
  Loaded: 'tcloaded',
  CmpShown: 'cmpuishown',
  UserActionComplete: 'useractioncomplete',
};

/** @enum {string} CmpStatus */
export const CmpStatus = {
  Stub: 'stub',
  Loaded: 'loaded',
  Error: 'error',
};

/**
 * Wrapper for the TCFv2 API that ensure we have CMP consent - either by prompting the user, or if
 * consent was already given prior to the current pageview.
 *
 * @return {Promise<{tcData: TCData}>}
 */
export const getTcfConsent = () => {
  return new Promise((resolve, reject) => {
    const { __tcfapi: tcfapi } = /** @type {ExtendedWindow} */ (window);
    if (!tcfapi) {
      return reject(new Error(`Could not find __tcfapi, continuing anyway.`));
    }

    tcfapi('addEventListener', 2, (tcData, success) => {
      const { eventStatus, gdprApplies, listenerId } = tcData;
      log(`tcfConsentHandler: `, {
        success,
        gdprApplies,
        eventStatus,
      });

      if (!success) {
        return reject(new Error(`User rejected consent`));
      }

      if (!gdprApplies) {
        return resolve({ tcData });
      }

      if (eventStatus === Event.Loaded || eventStatus === Event.UserActionComplete) {
        log(`OneTrust consent received:`, tcData);
        resolve({
          // TODO: Here we can return some state of consent given, so the caller can decide whether
          // permissions are enough. Idea for improved API eventually (e.g. if the consent given is
          // insufficient for the caller's use case.)
          tcData,
        });

        tcfapi('removeEventListener', 2, listenerId);
      } else if (eventStatus === Event.CmpShown) {
        log(`CMP is shown, awaiting consent...`);
      } else {
        log(`Received invalid eventStatus: ${eventStatus}`);
      }
    });
  });
};
